/**
 * Data Transformation Utilities
 *
 * Centralized data transformation functions for charts and analytics.
 * Extracts business logic from components for better testability and reusability.
 */

import { TrafficMetrics } from "../types";

/**
 * Chart data point structure
 */
export interface ChartDataPoint {
  time: string;
  displayTime?: string;
  timeValue: number;
  entries: number;
  exits: number;
  cars: number;
  bikes: number;
  occupancy: number;
}

/**
 * Traffic statistics structure
 */
export interface TrafficStats {
  totalEntries: number;
  totalExits: number;
  totalCars: number;
  totalBikes: number;
  peakEntry: { time: string; entries: number };
  peakExit: { time: string; exits: number };
}

/**
 * View type for analytics
 */
export type ViewType = "daily" | "monthly" | "yearly";

/**
 * Transform traffic metrics into chart data based on view type
 *
 * @param trafficMetrics - Raw traffic metrics from API
 * @param viewType - Current view (daily/monthly/yearly)
 * @returns Transformed chart data points
 *
 * @example
 * ```tsx
 * const chartData = transformTrafficData(metrics.traffic, "daily");
 * ```
 */
export const transformTrafficData = (
  trafficMetrics: TrafficMetrics | undefined,
  viewType: ViewType
): ChartDataPoint[] => {
  // Generate empty data points if no metrics available
  if (!trafficMetrics) {
    return generateEmptyDataPoints(viewType);
  }

  switch (viewType) {
    case "daily": {
      const distribution = trafficMetrics.hourlyDistribution || {};
      const data = Object.entries(distribution)
        .map(([hour, data]) => ({
          time: `${hour}:00`,
          timeValue: parseInt(hour),
          entries: data.entries.total,
          exits: data.exits.total,
          cars: data.entries.byVehicleType?.Car || 0,
          bikes: data.entries.byVehicleType?.Bike || 0,
          occupancy: data.occupancy,
        }))
        .sort((a, b) => a.timeValue - b.timeValue);

      return data.length > 0 ? data : generateEmptyDataPoints(viewType);
    }

    case "monthly": {
      const distribution = trafficMetrics.dailyDistribution || {};
      const data = Object.entries(distribution)
        .map(([day, data]) => {
          const dayNum = parseInt(day);
          return {
            time: formatMonthlyDate(dayNum),
            displayTime: `Day ${day}`,
            timeValue: dayNum,
            entries: data.entries.total,
            exits: data.exits.total,
            cars: data.entries.byVehicleType?.Car || 0,
            bikes: data.entries.byVehicleType?.Bike || 0,
            occupancy: data.occupancy,
          };
        })
        .sort((a, b) => a.timeValue - b.timeValue);

      return data.length > 0 ? data : generateEmptyDataPoints(viewType);
    }

    case "yearly": {
      const distribution = trafficMetrics.monthlyDistribution || {};
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const data = Object.entries(distribution)
        .map(([month, data]) => ({
          time: monthNames[parseInt(month) - 1],
          timeValue: parseInt(month),
          entries: data.entries.total,
          exits: data.exits.total,
          cars: data.entries.byVehicleType?.Car || 0,
          bikes: data.entries.byVehicleType?.Bike || 0,
          occupancy: data.occupancy,
        }))
        .sort((a, b) => a.timeValue - b.timeValue);

      return data.length > 0 ? data : generateEmptyDataPoints(viewType);
    }
  }
};

/**
 * Generate empty data points with 0 values for display
 *
 * @param viewType - Current view type
 * @returns Array of data points with 0 values
 */
const generateEmptyDataPoints = (viewType: ViewType): ChartDataPoint[] => {
  switch (viewType) {
    case "daily":
      // Generate 24 hours with 0 values (show key hours)
      return [0, 6, 12, 18].map((hour) => ({
        time: `${hour}:00`,
        timeValue: hour,
        entries: 0,
        exits: 0,
        cars: 0,
        bikes: 0,
        occupancy: 0,
      }));

    case "monthly":
      // Generate sample days with 0 values
      return [1, 8, 15, 22, 28].map((day) => ({
        time: formatMonthlyDate(day),
        displayTime: `Day ${day}`,
        timeValue: day,
        entries: 0,
        exits: 0,
        cars: 0,
        bikes: 0,
        occupancy: 0,
      }));

    case "yearly":
      // Generate all 12 months with 0 values
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return monthNames.map((month, index) => ({
        time: month,
        timeValue: index + 1,
        entries: 0,
        exits: 0,
        cars: 0,
        bikes: 0,
        occupancy: 0,
      }));
  }
};

/**
 * Calculate traffic statistics from chart data
 *
 * @param chartData - Transformed chart data points
 * @returns Aggregated traffic statistics
 *
 * @example
 * ```tsx
 * const stats = calculateTrafficStats(chartData);
 * console.log(`Peak entry time: ${stats.peakEntry.time}`);
 * ```
 */
export const calculateTrafficStats = (
  chartData: ChartDataPoint[]
): TrafficStats => {
  if (!chartData.length) {
    return {
      totalEntries: 0,
      totalExits: 0,
      totalCars: 0,
      totalBikes: 0,
      peakEntry: { time: "N/A", entries: 0 },
      peakExit: { time: "N/A", exits: 0 },
    };
  }

  const totalEntries = chartData.reduce((sum, d) => sum + d.entries, 0);
  const totalExits = chartData.reduce((sum, d) => sum + d.exits, 0);
  const totalCars = chartData.reduce((sum, d) => sum + d.cars, 0);
  const totalBikes = chartData.reduce((sum, d) => sum + d.bikes, 0);

  const peakEntry = chartData.reduce(
    (max, d) => (d.entries > max.entries ? d : max),
    chartData[0]
  );

  const peakExit = chartData.reduce(
    (max, d) => (d.exits > max.exits ? d : max),
    chartData[0]
  );

  return {
    totalEntries,
    totalExits,
    totalCars,
    totalBikes,
    peakEntry: { time: peakEntry.time, entries: peakEntry.entries },
    peakExit: { time: peakExit.time, exits: peakExit.exits },
  };
};

/**
 * Format monthly date (day number to date string)
 *
 * @param day - Day number (1-31)
 * @returns Formatted date string
 *
 * @example
 * ```tsx
 * formatMonthlyDate(15) // "Dec 15"
 * ```
 */
export const formatMonthlyDate = (day: number): string => {
  const now = new Date();
  const month = now.toLocaleString("default", { month: "short" });
  return `${month} ${day}`;
};

/**
 * Format chart axis label based on view type
 *
 * @param value - Raw value from data
 * @param viewType - Current view type
 * @returns Formatted label for chart axis
 *
 * @example
 * ```tsx
 * formatChartLabel(14, "daily") // "14:00"
 * formatChartLabel(5, "monthly") // "Dec 5"
 * formatChartLabel(3, "yearly") // "Mar"
 * ```
 */
export const formatChartLabel = (value: number, viewType: ViewType): string => {
  switch (viewType) {
    case "daily":
      return `${value}:00`;
    case "monthly":
      return formatMonthlyDate(value);
    case "yearly": {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return monthNames[value - 1] || "";
    }
  }
};
